import Flex from "../../components/Flex";
import AllergiesBlock from "../_shared/AllergiesBlock";
import HistoryBlock from "../_shared/HistoryBlock";
import MedicationsBlock from "../_shared/MedicationsBlock";
import PsychoeducationSection from "../_shared/PsychoeducationSection";
import RosBlock from "../_shared/RosBlock";
import ScreeningAssessmentBlock from "../_shared/ScreeningAssessmentBlock";
import {
  checkNull,
  CodeList,
  MENTAL_STATUS_EXAM_FIELDS,
  PLAN_FIELDS,
  PSYCH_PROGRESS_REPORT_LABEL,
  PSYCH_PROGRESS_SOAP_SECTION,
  RenderTextWithContainerPattern,
  ReportContainer,
  ReportSection,
} from "../constant";

interface PsychiatricProgressTemplateProps {
  data: any;
}

const PsychiatricProgressTemplate = ({
  data,
}: PsychiatricProgressTemplateProps) => {
  const hasMentalStatusExam =
    MENTAL_STATUS_EXAM_FIELDS.some((field) => !!data?.[field.key]?.trim()) ||
    !!data?.insightJudgment?.trim();

  // ReportContainer always renders its header, so the group is skipped
  // entirely when none of the child blocks have rows.
  const hasPatientReportedHistory =
    checkNull(data?.patientAllergies) ||
    checkNull(data?.medicalHistories) ||
    checkNull(data?.surgicalHistory) ||
    checkNull(data?.familyHistories) ||
    checkNull(data?.patientSocialHistory) ||
    checkNull(data?.patientMedications);

  return (
    <>
      {/* ── Subjective ── */}
      <ReportSection label={PSYCH_PROGRESS_SOAP_SECTION.SUBJECTIVE}>
        {/* Chief Complaint is captured in the encounter header, so
            EncounterTemplate prints it above these bands for every note type. */}
        <RenderTextWithContainerPattern
          label={PSYCH_PROGRESS_REPORT_LABEL.SUBJECTIVE_NOTE}
          val={data?.subjectiveChiefComplaint}
        />

        <RenderTextWithContainerPattern
          label={PSYCH_PROGRESS_REPORT_LABEL.HISTORY_OF_PRESENT_ILLNESS}
          val={data?.hpi}
        />

        <RosBlock data={data} />

        {/* Allergies, history and medications — what the patient reports
            during the visit — under one header. */}
        {hasPatientReportedHistory && (
          <ReportContainer
            label={PSYCH_PROGRESS_REPORT_LABEL.PATIENT_REPORTED_HISTORY}
            flow
          >
            <Flex flexDirection="column" gap={2} mt={1.5} p={1}>
              <AllergiesBlock data={data} />
              <HistoryBlock data={data} />
              <MedicationsBlock data={data} />
            </Flex>
          </ReportContainer>
        )}
      </ReportSection>

      {/* ── Objective ── */}
      <ReportSection label={PSYCH_PROGRESS_SOAP_SECTION.OBJECTIVE}>
        {/* Appearance → Insight & Judgment, grouped under the MSE header the
            same way the encounter form groups them. `flow` lets the card break
            across pages; without it nine free-text fields jump to the next page
            whole and strand blank space under the band. */}
        {hasMentalStatusExam && (
          <ReportContainer
            label={PSYCH_PROGRESS_REPORT_LABEL.MENTAL_STATUS_EXAM}
            flow
          >
            {MENTAL_STATUS_EXAM_FIELDS.map((field) => (
              <RenderTextWithContainerPattern
                key={field.key}
                label={field.label}
                val={data?.[field.key]}
              />
            ))}

            <RenderTextWithContainerPattern
              label={PSYCH_PROGRESS_REPORT_LABEL.INSIGHT_JUDGMENT}
              val={data?.insightJudgment}
            />
          </ReportContainer>
        )}
      </ReportSection>

      {/* ── Assessment ── */}
      <ReportSection label={PSYCH_PROGRESS_SOAP_SECTION.ASSESSMENT}>
        <RenderTextWithContainerPattern
          label={PSYCH_PROGRESS_REPORT_LABEL.ASSESSMENT}
          val={data?.assessmentFreeNote}
        />

        {/* Screeners are captured under the Assessment free text in the
            encounter form, so they print in this band rather than Objective
            like the other note types. */}
        <ScreeningAssessmentBlock data={data} />

        {/* ICD codes close the Assessment band. */}
        {checkNull(data?.assessmentCodes) && (
          <ReportContainer label={PSYCH_PROGRESS_REPORT_LABEL.ICD_CODE} flow>
            <CodeList codes={data?.assessmentCodes} />
          </ReportContainer>
        )}
      </ReportSection>

      {/* ── Plan ──
          Everything the encounter form captures below the Plan free text lives
          in this band, Follow Up and the Psychoeducation Session included. */}
      <ReportSection label={PSYCH_PROGRESS_SOAP_SECTION.PLAN}>
        <RenderTextWithContainerPattern
          label={PSYCH_PROGRESS_REPORT_LABEL.THERAPY}
          val={data?.therapy}
        />

        {/* Plan → Safety */}
        {PLAN_FIELDS.map((field) => (
          <RenderTextWithContainerPattern
            key={field.key}
            label={field.label}
            val={data?.[field.key]}
          />
        ))}

        <RenderTextWithContainerPattern
          label={PSYCH_PROGRESS_REPORT_LABEL.ENTER_NOTE}
          val={data?.note}
        />

        {/* CPT codes sit directly below Enter Note, as in the encounter form. */}
        {checkNull(data?.procedureCodes) && (
          <ReportContainer label={PSYCH_PROGRESS_REPORT_LABEL.CPT_CODE} flow>
            <CodeList codes={data?.procedureCodes} />
          </ReportContainer>
        )}

        {/* Follow Up has no field in this note's encounter form, so it only
            prints when the encounter carries a value from elsewhere. */}
        <PsychoeducationSection data={data} />

        <RenderTextWithContainerPattern
          label={PSYCH_PROGRESS_REPORT_LABEL.FOLLOW_UP}
          val={data?.followUp}
        />
      </ReportSection>
    </>
  );
};

export default PsychiatricProgressTemplate;
